import type {
  GetPublicArticleRequest,
  GetPublicArticleResponse,
} from '@cash-web/protos/squareup/cash/supportarticles/api/loggedout/v1/service.pb'
import logger from '@cash-web/shared/util-logger'
import type { Region } from '@cash-web/shared-region-ui-context-main'
import type { CashRequestOptions } from '@cash-web/shared-util-fetch'
import { HttpError, post } from '@cash-web/shared-util-fetch'
import type { SearchResponse } from '@cash-web/support/feature-main'

type SearchPayload = {
  search_text: string
  per_page: number
  locale: { country_code: Region; language_code: string }
  page?: number
}

const bookshelfUrl = process.env['NEXT_PUBLIC_BOOKSHELF_URL']
const supportArticlesUrl = process.env['SUPPORT_ARTICLES_URL']

export async function searchArticles(payload: SearchPayload, signal?: AbortSignal): Promise<SearchResponse> {
  const data = {
    source: 'CASH_HELP',
    search_type: 'COMPLETE',
    document_kinds: ['CASH_HELP_ARTICLE'],
    page: payload.page ?? 1,
    search_text: payload.search_text,
    per_page: payload.per_page,
    locale: {
      country_code: payload.locale.country_code,
      language_code: payload.locale.language_code.toLowerCase(), // Bookshelf expects lowercase
    },
  }

  const formData = new FormData()
  formData.append('data', JSON.stringify(data))

  const response = await fetch(bookshelfUrl, {
    method: 'POST',
    body: formData,
    signal,
  })

  if (response.ok) {
    return response.json()
  } else {
    const body = await response.text()
    throw new HttpError(body, response.status, body)
  }
}

export const GET_ARTICLE_FROM_SUPPORT_ARTICLES_URL = `${supportArticlesUrl}/api/loggedout/v1/get`

export const getArticleFromSupportArticles = (
  request: GetPublicArticleRequest,
  options: CashRequestOptions = {}
): Promise<GetPublicArticleResponse> => {
  const requestWithLowercaseLocale: GetPublicArticleRequest = { ...request, locale: request.locale?.toLowerCase() }

  return post<GetPublicArticleRequest, GetPublicArticleResponse>(
    GET_ARTICLE_FROM_SUPPORT_ARTICLES_URL,
    requestWithLowercaseLocale,
    options
  ).then(response => {
    if (!response.article) {
      return { article: undefined }
    }

    return { article: response.article }
  })
}

type StatusComponent = {
  name: string
  status: string
}

export type StatusPageResponse = {
  components: StatusComponent[]
}

const statusPageApiUrl = process.env['NEXT_PUBLIC_STATUS_PAGE_API_URL']

export async function getStatusPage(signal?: AbortSignal): Promise<StatusPageResponse> {
  try {
    const response = await fetch(statusPageApiUrl, {
      method: 'GET',
      signal,
      credentials: 'omit', // Don't send cookies to third-party API
      mode: 'cors',
      cache: 'no-cache',
      headers: {
        Accept: 'application/json',
      },
    })

    if (response.ok) {
      return response.json()
    } else {
      const body = await response.text()
      throw new HttpError(body, response.status, body)
    }
  } catch (error) {
    logger.error(new Error(`Failed to fetch status page, error: ${error}`))
    return { components: [] }
  }
}
